<template>
  <DModal :show-close="false" :isBgOverlay="true" :appendToBody="true" :ariaLabel="headerTitle" @close="$emit('close')">
    <div class="sds text-dark" slot="insertComponent">
      <div class="sds__header" :style="{backgroundColor: color}">
        <h2>{{ headerTitle }}</h2>
        <div class="font-large icon__bg-circle sds__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="sds__body">
        <!-- Venstre: den normale brik-formular -->
        <div class="sds__form">
          <div class="sds__group">
            <label class="sds__label">Brikkens navn</label>
            <input type="text" v-model="name" class="sds__input" ref="input_name"
                   placeholder="Eks. Motionsdag" @keyup="nameError = false"
                   :class="{'sds__input--error': nameError}">
            <p class="sds__error" v-if="nameError">Brikken skal have et navn</p>
          </div>

          <div class="sds__group">
            <label class="sds__label">Klokketimer i skoleåret</label>
            <input type="number" min="0" v-model.number="hours" class="sds__input" @focus="selectAllOnFocus($event)">
          </div>

          <div class="sds__group">
            <label class="sds__label">Vælg farve</label>
            <ColorPicker :initialColor="color" @changedColor="color = $event"></ColorPicker>
          </div>

          <div class="sds__group">
            <label class="sds__label">Vælg briktype</label>
            <SubjectTypePicker calendarType="klass" :subjectType="subjectType" @change="subjectType = $event">
            </SubjectTypePicker>
          </div>
        </div>

        <!-- Højre: klasser med undervisere -->
        <div class="sds__classes">
          <div class="sds__classes__top">
            <div class="sds__classes__top__left">
              <Tabs class="sds__tabs" :tabs="modeTabs" :selected="mode" @select="setMode"></Tabs>
            </div>
            <div class="sds__classes__head">
              <button type="button" class="do-bttn do-bttn--small" v-if="mode === 'per_class'" @click="addRow">Tilføj brik</button>
            </div>
          </div>
          <p class="sds__error" v-if="rowsError">Vælg mindst én klasse</p>

          <div class="sds__table">
            <div class="sds__table__head" v-if="rows.length > 0 || existingSubjects.length > 0">
              <div class="sds__col sds__col--title">Brik</div>
              <div class="sds__col sds__col--class">Klasser</div>
              <div class="sds__col sds__col--teachers">Undervisere</div>
              <div class="sds__col sds__col--hours">Timer</div>
              <div class="sds__col sds__col--remove"></div>
            </div>
            <div v-for="subject in existingSubjects" :key="'ex-' + subject.id" class="sds__row sds__row--existing">
              <div class="sds__col sds__col--title">
                <span class="sds__pill" :style="{backgroundColor: subject.color}">{{ subject.name }}</span>
              </div>
              <div class="sds__col sds__col--class">{{ existingKlasseNames(subject) || '–' }}</div>
              <div class="sds__col sds__col--teachers">{{ existingTeacherNames(subject) || '–' }}</div>
              <div class="sds__col sds__col--hours">{{ formatHours(subject.hours) }}</div>
              <div class="sds__col sds__col--remove">
                <span class="sds__row__badge">Oprettet</span>
              </div>
            </div>
            <div v-for="row in rows" :key="row.uid" class="sds__row"
                 :class="{'sds__row--empty': row.klassIds.length === 0, 'sds__row--error': row.klassError}">
              <div class="sds__col sds__col--title">
                <span class="sds__pill" :style="{backgroundColor: color}">{{ name || 'Brik' }}</span>
              </div>
              <div class="sds__col sds__col--class">
                <DSelectCalendarIds
                  :key="'k-' + row.uid"
                  itemType="klass"
                  :showTags="false"
                  :preSelectedItems="row.klassIds"
                  :showCreateButton="false"
                  @update="setKlasses(row, $event)">
                </DSelectCalendarIds>
                <p class="sds__error sds__error--row" v-if="row.klassError">Brikken skal have minimum en klasse</p>
              </div>
              <div class="sds__col sds__col--teachers">
                <DSelectCalendarIds
                  :key="'t-' + row.uid"
                  itemType="user"
                  :showTags="true"
                  :preSelectedItems="row.teacherIds"
                  :showCreateButton="false"
                  @update="setTeachers(row, $event)">
                </DSelectCalendarIds>
              </div>
              <div class="sds__col sds__col--hours">
                <input type="number" min="0" v-model.number="row.hours" class="sds__input"
                       @focus="selectAllOnFocus($event)">
              </div>
              <div class="sds__col sds__col--remove">
                <div class="icon__bg-circle sds__row__remove" v-if="mode === 'per_class'"
                     role="button" tabindex="0" aria-label="Fjern række"
                     @click="removeRow(row)" @keydown.enter="removeRow(row)">
                  <span class="icon-bin"></span>
                </div>
              </div>
            </div>
            <p class="sds__hint" v-if="rows.length === 0">Ingen klasser tilføjet endnu.</p>
          </div>
        </div>
      </div>

      <div class="sds__footer">
        <div class="sds__footer__warnings">
          <p class="sds__footer__warning" v-if="duplicateKlassNames.length > 0">
            <span class="icon-warning"></span>
            Flere brikker har samme klasse: {{ duplicateKlassNames.join(', ') }}
          </p>
          <p class="sds__footer__warning" v-if="missingKlassNames.length > 0">
            <span class="icon-warning"></span>
            Mangler klasse: {{ missingKlassNames.join(', ') }}
          </p>
        </div>
        <button class="do-bttn" @click="$emit('close')" :disabled="isSaving">Annuller</button>
        <button
          class="do-bttn do-bttn--submit"
          :class="{
            'do-bttn--submit--disabled': isSaving,
            'do-bttn--submit--loading': isSaving
          }"
          @click="create">Opret {{ brikLabel }}</button>
      </div>
    </div>
  </DModal>
</template>

<script>
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import DModal from '../common/DModal'
import ColorPicker from '../Subjects/ColorPicker'
import SubjectTypePicker from '../Subjects/SubjectFormSubjectType'
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import Tabs from '../common/Tabs'

export default {
  name: 'special-day-subjects-modal',
  components: {DModal, ColorPicker, SubjectTypePicker, DSelectCalendarIds, Tabs},
  props: {
    schoolId: {
      default: null
    },
    schoolYearId: {
      default: null
    },
    // Den omlagte dag der oprettes brikker for.
    specialDay: {
      type: Object,
      default: () => ({})
    },
    klassCalendars: {
      type: Array,
      default: () => []
    },
    // Allerede oprettede brikker for dagen — vises skrivebeskyttet øverst.
    existingSubjects: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isSaving: false,
      name: this.specialDay && this.specialDay.title ? this.specialDay.title : '',
      hours: 0,
      color: '#A0A5EF',
      subjectType: 'subject',
      mode: 'per_class',
      rows: [],
      // Hver visning husker sine egne undervisere uafhængigt. Her gemmer vi et
      // snapshot af rækkerne (klasser + undervisere) pr. visning ved skift.
      savedRowsByMode: {per_class: null, combined: null},
      rowUid: 0,
      originalKlassIds: [],
      nameError: false,
      rowsError: false
    }
  },
  computed: {
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    existingKlassIds () {
      const ids = []
      this.existingSubjects.forEach(subject => {
        const subjectIds = []
        if (subject.calendar_id) {
          subjectIds.push(subject.calendar_id)
        }
        if (Array.isArray(subject.calendar_ids)) {
          subject.calendar_ids.forEach(id => subjectIds.push(id))
        }
        subjectIds.forEach(id => {
          if (this.klassCalendars.some(k => k.id === id) && ids.indexOf(id) === -1) {
            ids.push(id)
          }
        })
      })
      return ids
    },
    headerTitle () {
      return this.name && this.name.length > 0 ? this.name : 'Opret brikker'
    },
    modeTabs () {
      return [
        {id: 'per_class', title: 'En brik pr. klasse'},
        {id: 'combined', title: 'En samlet brik'}
      ]
    },
    subjectCount () {
      return this.rows.filter(r => r.klassIds.length > 0).length
    },
    brikLabel () {
      return this.subjectCount === 1 ? '1 brik' : this.subjectCount + ' brikker'
    },
    duplicateKlassNames () {
      const counts = {}
      this.rows.forEach(row => {
        row.klassIds.forEach(id => {
          counts[id] = (counts[id] || 0) + 1
        })
      })
      return Object.keys(counts)
        .filter(id => counts[id] > 1)
        .map(id => this.klassName(Number(id)))
    },
    missingKlassNames () {
      const used = this.existingKlassIds.slice()
      this.rows.forEach(row => {
        row.klassIds.forEach(id => {
          if (used.indexOf(id) === -1) {
            used.push(id)
          }
        })
      })
      return this.originalKlassIds
        .filter(id => used.indexOf(id) === -1)
        .map(id => this.klassName(id))
    }
  },
  watch: {
    // Venstre felt sætter timetallet samlet for alle brik-linjer.
    hours (val) {
      this.rows.forEach(row => {
        row.hours = val
      })
    }
  },
  mounted () {
    const ids = (this.specialDay && this.specialDay.calendar_ids) || []
    const klassIds = ids
      .filter(id => this.klassCalendars.some(k => k.id === id))
      .sort((a, b) => this.klassName(a).localeCompare(this.klassName(b), 'da'))
    this.originalKlassIds = klassIds.slice()
    if (this.existingSubjects.length > 0) {
      // Der findes allerede brikker for dagen — de vises skrivebeskyttet øverst,
      // så vi starter med en blank række til nye brikker.
      this.addRow()
    } else if (klassIds.length > 0) {
      klassIds.forEach(id => this.rows.push({uid: this.rowUid++, klassIds: [id], teacherIds: [], hours: this.hours}))
    } else {
      this.addRow()
    }
    this.$nextTick(() => {
      if (this.$refs.input_name) {
        this.$refs.input_name.focus()
      }
    })
    document.body.classList.add('no-scroll')
  },
  beforeDestroy () {
    document.body.classList.remove('no-scroll')
  },
  methods: {
    klassName (id) {
      const klass = this.klassCalendars.find(k => k.id === id)
      return klass && klass.name ? klass.name : ''
    },
    formatHours (hours) {
      const num = parseFloat(hours)
      return isNaN(num) ? '0' : String(num)
    },
    existingKlasseNames (subject) {
      const ids = []
      if (subject.calendar_id) {
        ids.push(subject.calendar_id)
      }
      if (Array.isArray(subject.calendar_ids)) {
        subject.calendar_ids.forEach(id => ids.push(id))
      }
      const seen = {}
      const names = []
      ids.forEach(id => {
        if (seen[id]) {
          return
        }
        seen[id] = true
        const name = this.klassName(id)
        if (name) {
          names.push(name)
        }
      })
      return names.join(', ')
    },
    existingTeacherNames (subject) {
      const ids = Array.isArray(subject.calendar_ids) ? subject.calendar_ids : []
      const seen = {}
      const names = []
      ids.forEach(id => {
        const cal = this.allCalendarsInHash[id]
        if (seen[id] || !cal || cal.type !== 'user' || !cal.name) {
          return
        }
        seen[id] = true
        names.push(cal.name)
      })
      return names.join(', ')
    },
    setMode (mode) {
      if (mode === this.mode) {
        return
      }
      // Gem den nuværende visnings rækker, så dens undervisere kan gendannes senere.
      this.savedRowsByMode[this.mode] = this.rows.map(r => ({
        klassIds: r.klassIds.slice(),
        teacherIds: r.teacherIds.slice()
      }))

      // Klasserne synkroniseres på tværs af visninger (samlet sæt fra nuværende rækker).
      const klassIds = []
      this.rows.forEach(r => {
        r.klassIds.forEach(id => {
          if (klassIds.indexOf(id) === -1) {
            klassIds.push(id)
          }
        })
      })
      klassIds.sort((a, b) => this.klassName(a).localeCompare(this.klassName(b), 'da'))

      // Undervisere gendannes fra den valgte visnings egen hukommelse — de føres
      // IKKE med over fra den anden visning.
      const saved = this.savedRowsByMode[mode]
      if (mode === 'combined') {
        const teacherIds = saved && saved.length > 0 ? saved[0].teacherIds.slice() : []
        this.rows = [{uid: this.rowUid++, klassIds: klassIds, teacherIds: teacherIds, hours: this.hours}]
      } else {
        if (klassIds.length > 0) {
          this.rows = klassIds.map(id => {
            const savedRow = saved ? saved.find(r => r.klassIds.indexOf(id) > -1) : null
            const teacherIds = savedRow ? savedRow.teacherIds.slice() : []
            return {uid: this.rowUid++, klassIds: [id], teacherIds: teacherIds, hours: this.hours}
          })
        } else {
          this.rows = [{uid: this.rowUid++, klassIds: [], teacherIds: [], hours: this.hours}]
        }
      }
      this.mode = mode
    },
    selectAllOnFocus (evt) {
      setTimeout(() => evt.target.select(), 0)
    },
    addRow () {
      this.rows.unshift({uid: this.rowUid++, klassIds: [], teacherIds: [], hours: this.hours})
    },
    removeRow (row) {
      const idx = this.rows.indexOf(row)
      if (idx > -1) {
        this.rows.splice(idx, 1)
      }
    },
    setKlasses (row, klasses) {
      row.klassIds = klasses.map(k => k.id)
      if (row.klassIds.length > 0) {
        this.$set(row, 'klassError', false)
      }
      this.rowsError = false
    },
    setTeachers (row, teachers) {
      row.teacherIds = teachers.map(t => t.id)
      if (row.teacherIds.length === 0) {
        this.$set(row, 'klassError', false)
      }
    },
    validate () {
      this.nameError = !this.name || this.name.trim().length === 0
      // En række med undervisere (eller timer) men uden klasse er en fejl —
      // den markeres rød i stedet for at blive droppet lydløst.
      let hasRowError = false
      this.rows.forEach(row => {
        const isError = row.klassIds.length === 0 && row.teacherIds.length > 0
        this.$set(row, 'klassError', isError)
        if (isError) {
          hasRowError = true
        }
      })
      const validRows = this.rows.filter(r => r.klassIds.length > 0)
      this.rowsError = validRows.length === 0
      return !this.nameError && !this.rowsError && !hasRowError
    },
    subjectBase () {
      return {
        name: this.name.trim(),
        color: this.color,
        default_duration: 60,
        subject_type: this.subjectType,
        special_day_id: this.specialDay ? this.specialDay.id : null,
        from_date: this.specialDay ? this.specialDay.start_date : null,
        to_date: this.specialDay ? this.specialDay.end_date : null
      }
    },
    buildSubjects () {
      // Hver brik-linje bliver til én brik. Første klasse er master (calendar_id),
      // og de øvrige klasser + undervisere lægges i calendar_ids. Master-kalenderen
      // udelades af calendar_ids, da backenden selv opretter dens subject_calendar.
      return this.rows
        .filter(r => r.klassIds.length > 0)
        .map(r => Object.assign({}, this.subjectBase(), {
          calendar_id: r.klassIds[0],
          calendar_ids: r.klassIds.slice(1).concat(r.teacherIds),
          hours: Number(r.hours) || 0
        }))
    },
    create () {
      if (this.isSaving || !this.validate()) {
        return
      }
      this.isSaving = true
      const subjects = this.buildSubjects()
      docendo.bulkCreateSubjects(this.schoolId, this.schoolYearId, subjects, data => {
        if (data && data.status === 'error') {
          this.onError()
          return
        }
        this.isSaving = false
        const errors = data && data.errors ? data.errors : []
        if (errors.length > 0) {
          eventbus.toast = {text: 'Nogle brikker kunne ikke oprettes', success: false}
        } else {
          eventbus.toast = {text: 'Brikkerne blev oprettet', success: true}
        }
        this.$emit('created')
      }, this.onError)
    },
    onError () {
      this.isSaving = false
      eventbus.toast = {text: 'Noget gik galt. Prøv igen.', success: false}
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.sds {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f6f9fc;
}

.sds__header {
  position: relative;
  padding: 1.5rem 2rem;
  color: #fff;
  transition: background-color 0.4s;
}

.sds__header h2 {
  margin: 0;
  color: #fff;
}

.sds__header__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  font-size: 1.2rem;
  cursor: pointer;
}

.sds__body {
  flex: 1;
  display: flex;
  gap: 2rem;
  overflow: hidden;
  min-height: 0;
  background: #f6f9fc;
}

.sds__form {
  flex: 0 0 360px;
  min-width: 0;
  padding: 1rem;
}

.sds__classes {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-left: 1px solid var(--ligthGrey);
  padding: 1rem;
  background: #fff;
}

.sds__group {
  margin-bottom: 1.25rem;
}

.sds__label {
  display: block;
  letter-spacing: .05rem;
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 700;
  color: #a2a3a6;
  margin-bottom: 0.4rem;
}

.sds__label--small {
  font-size: 0.7rem;
  margin-bottom: 0.25rem;
}

.sds__input {
  width: 100%;
  height: 38px;
  background-color: #fff;
  border: 1px solid #edf2f7;
  border-radius: 4px;
  padding: 0 0.5rem;
  box-shadow: 0 2px 5px -1px rgba(50, 50, 93, .25), 0 1px 3px -1px rgba(0, 0, 0, .3);
}

.sds__input--error {
  border-color: var(--danger);
}

.sds__error {
  color: var(--danger);
  font-size: 12px;
  margin: 0.4rem 0 0 0;
}

.sds__classes__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.sds__classes__top__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sds__classes__head {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sds__subtitle {
  margin: 0;
  font-size: 16px;
}

.sds__link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  color: var(--blue);
  text-decoration: underline;
}

.sds__link:hover {
  color: var(--dark-blue);
}

.sds__table {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sds__table__head {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0 0.5rem;
}

.sds__table__head .sds__col {
  letter-spacing: .05rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 700;
  color: #a2a3a6;
}

.sds__row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  transition: background-color 0.15s;
}

.sds__row:hover {
  background: aliceblue;
}

.sds__row--empty:hover {
  background: #fff;
}

.sds__row--error {
  border-color: var(--danger);
}

.sds__error--row {
  margin-top: 0.25rem;
}

.sds__row--existing {
  background: var(--doGrey);
  align-items: center;
}

.sds__row--existing:hover {
  background: var(--doGrey);
}

.sds__row--existing .sds__col--class,
.sds__row--existing .sds__col--teachers,
.sds__row--existing .sds__col--hours {
  font-size: 13px;
  color: var(--grey);
}

.sds__row__badge {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: .05rem;
  color: var(--grey);
  white-space: nowrap;
}

.sds__col--title {
  flex: 0 0 160px;
  min-width: 0;
}

.sds__pill {
  display: flex;
  align-items: center;
  max-width: 100%;
  height: 30px;
  padding: 4px 6px;
  border-radius: 4px;
  color: #fff;
  font-weight: bold;
  text-align: left;
  word-break: break-all;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  transition: background-color 0.3s;
}

.sds__col--class {
  flex: 1;
  min-width: 0;
}

.sds__col--teachers {
  flex: 1;
  min-width: 0;
}

.sds__col--hours {
  flex: 0 0 90px;
}

.sds__col--hours .sds__input {
  text-align: right;
}

.sds__col--remove {
  flex: 0 0 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sds__row__remove {
  color: var(--danger);
  cursor: pointer;
}

.sds__hint {
  color: var(--grey);
  font-size: 13px;
}

.sds__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 2rem;
  background: #fff;
  border-top: 1px solid var(--ligthGrey);
}

.sds__footer__warnings {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sds__footer__warning {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--danger);
  font-size: 13px;
}

.sds >>> .d-select__input-look {
  min-height: 38px;
  box-shadow: none;
  border: 1px solid #acb6be;
}

.sds >>> .d-select__input-look.active {
  border-color: var(--blue);
}

.sds >>> .d-select__header__search,
.sds >>> .d-select__header__search.show.input-active {
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// SpecialDaySubjectsModal.vue?02b669cf